import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import {
  ChangeEvent,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { usePostSettlementStateChangeApplicationUploadMutation } from "../../features/api";
import { Messages } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import {
  clearFormFieldErrTxt,
  createFormFieldErrTxts,
} from "../../features/error/validation-slice";
import { getExt } from "../../utils/common";
import { downloadBase64File } from "../../utils/downLoad";
import { formatLabel } from "../../utils/format";
import { getErrorText } from "../../utils/inputValidation";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import ErrorTable, {
  MessageItem,
  getMessageItemList,
} from "../UI/table/ErrorTable";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import { APPLICATION_SHOP_TYPE_LIST, CONFIGS, LABELS } from "./constants";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * 加盟店申請（包括）画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const errorRef = createRef<HTMLDivElement>();
  const { formFieldErrTxts } = useAppSelector((state) => state.validation);
  const [uploadSettlementStateChangeApplicationFiles] =
    usePostSettlementStateChangeApplicationUploadMutation();
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined);
  const [errorText, setErrorText] = useState("");
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [applicationShopType, setApplicationShopType] =
    useState<SelectProps.Option>({
      label: "",
      value: "",
    });
  const [errorMessageList, setErrorMessageList] = useState<MessageItem[]>([]);

  /**
   * ref.current.scrollIntoView() を呼び出してスクロール
   */
  const scrollToBottomOfList = useCallback(() => {
    errorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [errorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
  }, [errorMessageList, scrollToBottomOfList]);

  /**
   * 申請店舗種別とファイルの入力チェック
   * @param {File | undefined} uploadFile
   * @returns {boolean} true: 入力エラー
   */
  const isInputError = (uploadFile: File | undefined) => {
    if (!applicationShopType.value) {
      dispatch(
        createFormFieldErrTxts({
          name: "selectItem",
          msgId: "KGRERR023",
          msgArgs: [LABELS.APPLICATION_SHOP_TYPE],
        })
      );
    } else {
      dispatch(clearFormFieldErrTxt("selectItem"));
    }
    if (!uploadFile) {
      const messageId = "KGRERR054" as keyof Messages;
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
    }
    if (!applicationShopType.value || !uploadFile) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * 確認ボタンOK押下時の処理.
   * @param {FormData} fileData ファイル
   */
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    setErrorText("");

    // 決済状態変更申請ファイルAPI呼び出し
    uploadSettlementStateChangeApplicationFiles(fileData)
      .unwrap()
      .then((res) => {
        setErrorMessageList([]);

        // アップロード後初期化
        setUploadFile(undefined);

        if (res.status === COMMON_CONFIGS.API.STATUS_ERROR) {
          if (res.errorMessageList?.length !== 0) {
            setErrorMessageList(getMessageItemList(res.errorMessageList));
          }
          if (res.item) {
            downloadBase64File(res.item);
          }
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  /**
   * 申請ボタン押下処理
   */
  const onClickButton = async () => {
    if (isInputError(uploadFile)) {
      return;
    }

    const fileData = new FormData();
    fileData.append(
      CONFIGS.APPLICATION_SHOP_TYPE_CODE,
      applicationShopType.value!
    );
    fileData.append(CONFIGS.FILE, uploadFile!);

    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content:
          uploadFile !== undefined
            ? formatLabel(
                `${COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM} ${LABELS.APPLICATION_CONFIRM}`,
                [uploadFile.name]
              )
            : "",
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          merchantApplication(fileData);
        },
      })
    );

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /**
   * 選択したファイルを設定する.
   * @param {ChangeEvent<HTMLInputElement>} event
   */
  const onChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files?.[0]) {
      return;
    }

    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      dispatch(
        addNotification({
          msgId: "KGRERR057",
          msgArgs: [],
        })
      );
      return;
    }

    setUploadFile(files[0]);
    setErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  /**
   * アップロードボタンイベント
   * @param {CustomEvent<ClickDetail>} event
   */
  const onUpload = (event: CustomEvent<ClickDetail>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <Container
      header={
        <Header variant={"h2"} description={""}>
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction={"vertical"} size={"xxl"}>
        <Form
          actions={
            <SpaceBetween direction={"horizontal"} size={"l"}>
              {isSpinnerDisplay && <Spinner size={"big"} />}
              <Button
                iconAlign={"right"}
                onClick={onClickButton}
                variant={"primary"}
                disabled={isButtonDisabled}
              >
                {COMMON_LABELS.BTNS.APPLY}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction={"vertical"} size={"xxl"}>
            <ColumnLayout columns={4}>
              <FormField
                label={
                  <SpaceBetween direction={"horizontal"} size={"xs"}>
                    <Box variant={"strong"}>{LABELS.APPLICATION_SHOP_TYPE}</Box>
                    <Box variant={"small"} color={"text-status-error"}>
                      {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                    </Box>
                  </SpaceBetween>
                }
                errorText={getErrorText("selectItem", formFieldErrTxts)}
              >
                <Select
                  selectedOption={applicationShopType}
                  options={APPLICATION_SHOP_TYPE_LIST}
                  filteringType={"none"}
                  onChange={({ detail }) => {
                    dispatch(clearFormFieldErrTxt("selectItem"));
                    setApplicationShopType(detail.selectedOption);
                  }}
                />
              </FormField>
            </ColumnLayout>
            <FileUpload
              inputRef={inputRef}
              label={LABELS.STORE_INFO_APP_FORM}
              name={"applicationFile"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION}
              fileName={uploadFile ? uploadFile.name : ""}
              setUploadFile={setUploadFile}
              errorText={errorText}
              setErrorText={setErrorText}
              onChange={onChangeFile}
              onUpload={onUpload}
            />
          </SpaceBetween>
        </Form>
        <SpaceBetween direction={"vertical"} size={"xxs"}>
          {errorMessageList.length !== 0 ? (
            <>
              <ErrorTable data={errorMessageList} title={LABELS.ERROR_TITLE} />
              <div id={"error"} ref={errorRef} />
            </>
          ) : (
            <></>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};
